import React from 'react';
import clsx from 'clsx';
import _ from 'underscore';
import {Icon, faAngleDown} from '@netx/core-icons';
import {Typography} from '@mui/material';
import {unstable_capitalize} from '@mui/utils';
import {flexRender, type Header, type RowData} from '@tanstack/react-table';
import {useConstCallback} from '@netx/core-components/hooks/useConstCallback';
import {useDragGlobalStyles} from '@netx/core-components/hooks/useDragGlobalStyles';
import {
	composeClasses,
	styled,
	type WithClasses,
} from '@netx/core-components/Theme';
// import {
// 	VirtualizedTableHeaderCellDragger,
// 	virtualizedTableHeaderCellDraggerClasses,
// } from '@netx/core-components/VirtualizedTable/components/VirtualizedTableHeaderCell';
import {splitEventHandler} from '@netx/core-next/utils';
import {TableCell, type TableCellProps} from './TableCell';
import {
	TableHeaderCellResizer,
	tableHeaderCellResizerClasses,
} from './TableHeaderCellResizer';
import {
	getTableHeaderRowCellUtilityClass,
	TABLE_HEADER_ROW_CELL_CLASS_NAME,
	tableHeaderRowCellClasses,
	type TableHeaderRowCellClasses,
} from './tableHeaderRowCellClasses';

type TableHeaderRowCellOwnerState = WithClasses<
	TableHeaderRowCellClasses,
	{
		isSortable?: boolean;
		sortType?: 'numeric' | 'alphanumeric';
		isSorted?: 'asc' | 'desc' | false;
		isResizing?: boolean;
	}
>;

const useUtilityClasses = (ownerState: TableHeaderRowCellOwnerState) => {
	const {classes, isSortable, isResizing, isSorted, sortType} = ownerState;
	const slots = {
		root: [
			'root',
			isSortable && 'isSortable',
			isResizing && 'isResizing',
			isSorted && 'isSorted',
			isSorted && `sorted${unstable_capitalize(isSorted)}`,
			sortType && `sortType${unstable_capitalize(sortType)}`,
		],
		content: [
			'content',
			isSortable && 'isSortable',
			isResizing && 'isResizing',
			isSorted && 'isSorted',
			isSorted && `sorted${unstable_capitalize(isSorted)}`,
			sortType && `sortType${unstable_capitalize(sortType)}`,
		],
		sortIcon: ['sortIcon'],
	};
	return composeClasses(slots, getTableHeaderRowCellUtilityClass, classes);
};

const TableHeaderRowCellRoot = styled(TableCell, {
	name: TABLE_HEADER_ROW_CELL_CLASS_NAME,
	slot: 'Root',
})(({theme}) => ({
	height: 48,
	// borderWidth: `0 0 1px 0`,
	borderBottom: `1px solid transparent`,
	textAlign: 'left',
	//padding: 0,
	color: theme.palette.text.secondary,
	overflow: 'hidden',
	position: 'relative',
	// padding: theme.spacing(0, 1),
	zIndex: 2,
	outline: 'none',
	userSelect: 'none',
	[`&:focus`]: {
		borderBottom: `1px solid ${theme.palette.primary.main}`,
	},
	[`&:hover, &.${tableHeaderRowCellClasses.isResizing}`]: {
		[`.${tableHeaderCellResizerClasses.root}`]: {
			opacity: 1,
		},
	},
	[`&.${tableHeaderRowCellClasses.isResizing}`]: {
		color: theme.palette.text.primary,
		[`.${tableHeaderCellResizerClasses.root}`]: {
			borderColor: theme.palette.secondary.main,
		},
	},
	[`&.${tableHeaderRowCellClasses.sortedAsc}`]: {
		[`.${tableHeaderRowCellClasses.sortIcon}`]: {
			transform: 'rotate(180deg)',
		},
	},
	[`&.${tableHeaderRowCellClasses.sortedDesc}`]: {
		[`.${tableHeaderRowCellClasses.sortIcon}`]: {
			transform: 'rotate(0deg)',
		},
	},
	[`&:not(.${tableHeaderRowCellClasses.isSorted}):not(:hover)`]: {
		[`.${tableHeaderRowCellClasses.sortIcon}`]: {
			opacity: 0,
		},
	},
	[`&.${tableHeaderRowCellClasses.isSortable}`]: {
		cursor: 'pointer',
	},
	'&:hover, &:focus': {
		[`&.${tableHeaderRowCellClasses.isSortable}`]: {
			color: theme.palette.text.primary,
		},
		[`&:not(.${tableHeaderRowCellClasses.isSorted})`]: {
			[`.${tableHeaderRowCellClasses.sortIcon}`]: {
				opacity: 0.3,
			},
		},
	},
})) as <TData extends RowData>(props: TableCellProps<TData>) => JSX.Element;

const TableHeaderRowCellSortIcon = styled(Icon, {
	name: TABLE_HEADER_ROW_CELL_CLASS_NAME,
	slot: 'SortIcon',
})(({theme}) => ({
	flex: '0 0 auto',
	transition: theme.transitions.create('transform'),
}));

export interface TableHeaderRowCellProps<
	TData extends RowData,
> extends WithClasses<
	TableHeaderRowCellClasses,
	Omit<TableCellProps<TData>, 'columnId' | 'width'>
> {
	header: Header<TData, unknown>;
	height?: number;
}

export const TableHeaderRowCellPrimitive = <TData extends RowData>({
	classes: _classes,
	className,
	header,
	height,
	tabIndex,
	onClick,
	onFocusCapture,
	onKeyDown,
	...props
}: TableHeaderRowCellProps<TData>) => {
	const isResizing = header.column.getIsResizing();

	const isSorted = header.column.getIsSorted();
	const isSortable = header.column.getCanSort(); // && !props.disabled;
	const sortType =
		(header.column.columnDef.meta as any)?.sortType || 'alphanumeric';
	const horizontalAlign = (header.column.columnDef.meta as any)
		?.horizontalAlign;
	const isInteractive =
		isSortable ||
		((header.column.columnDef.meta as any)?.isInteractive === true &&
			!props.disabled);

	const ownerState: TableHeaderRowCellOwnerState = {
		classes: _classes,
		className,
		isResizing,
		isSortable,
		isSorted,
		sortType,
	};

	const classes = useUtilityClasses(ownerState);

	useDragGlobalStyles({
		enabled: isResizing,
		cursor: 'col-resize',
	});

	const handleResize = header.getResizeHandler();

	const lastClickTimeRef = React.useRef(0);
	const handlePotentialDoubleClick = useConstCallback(
		(canResize = false) =>
			(event: React.MouseEvent<HTMLDivElement>) => {
				const now = Date.now();
				if (lastClickTimeRef.current + 300 > now) {
					event.preventDefault();
					event.stopPropagation();
					header.column.resetSize();
					return;
				}
				canResize && handleResize(event);
				lastClickTimeRef.current = now;
			},
	);

	const toggleSort = useConstCallback(() => {
		if (isSorted === 'asc') {
			header.column.clearSorting();
			return;
		}
		header.column.toggleSorting(
			isSorted === false ? true : isSorted !== 'desc',
		);
	});

	const handleClick = useConstCallback(
		(event: React.MouseEvent<HTMLDivElement>) => {
			if (event.defaultPrevented) return;
			if (isSortable && isInteractive) toggleSort();
		},
	);

	const handleKeyDown = useConstCallback(
		(event: React.KeyboardEvent<HTMLDivElement>) => {
			if (!isSortable || !isInteractive || event.defaultPrevented) return;
			switch (event.key) {
				case 'Enter':
				case ' ':
					event.preventDefault();
					event.stopPropagation();
					toggleSort();
					break;
			}
		},
	);

	let title: string | undefined =
		typeof header.column.columnDef.header === 'string'
			? header.column.columnDef.header
			: header.column.columnDef.header?.(header.getContext())?.toString();

	if (title === '[object Object]') title = undefined;

	return (
		<TableHeaderRowCellRoot<TData>
			data-type={sortType}
			aria-sort={
				isSorted === 'asc'
					? 'ascending'
					: isSorted === 'desc'
						? 'descending'
						: 'none'
			}
			className={clsx(classes.root, className)}
			role="columnheader"
			aria-colindex={props.index + 1}
			columnId={header.id}
			onFocusCapture={onFocusCapture}
			onClick={isSortable ? splitEventHandler(onClick, handleClick) : onClick}
			width={header.getSize()}
			sortType={sortType}
			horizontalAlign={horizontalAlign}
			tabIndex={isInteractive ? tabIndex : undefined}
			title={title}
			{...props}
			onKeyDown={
				isSortable ? splitEventHandler(onKeyDown, handleKeyDown) : onKeyDown
			}
			style={{
				...props.style,
				height,
			}}
		>
			{header.isPlaceholder ? null : (
				<>
					{isSortable && sortType === 'numeric' && (
						<TableHeaderRowCellSortIcon
							className={classes.sortIcon}
							icon={faAngleDown}
							sx={{mr: 1}}
						/>
					)}

					{(header.column.columnDef.meta as any)?.disableTextWrapper ? (
						flexRender(header.column.columnDef.header, header.getContext())
					) : (
						<Typography noWrap variant="body2" sx={{pr: 0.5}}>
							{flexRender(header.column.columnDef.header, header.getContext())}
						</Typography>
					)}

					{isSortable && sortType !== 'numeric' && (
						<TableHeaderRowCellSortIcon
							className={classes.sortIcon}
							icon={faAngleDown}
							sx={{ml: 0.5}}
						/>
					)}

					{header.column.getCanResize() && (
						<TableHeaderCellResizer
							onMouseDown={handlePotentialDoubleClick(true)}
							onClick={handlePotentialDoubleClick(false)}
						/>
					)}
				</>
			)}
		</TableHeaderRowCellRoot>
	);
};

export const TableHeaderRowCell = TableHeaderRowCellPrimitive;

export default TableHeaderRowCell;
